import React from 'react';
import * as S from './styles';
import { DateText, DocumentLabel, DocumentNumber } from '../../components';
import { utcToGmt } from '../../components/utils';
import { CurrencyText } from '../../components/CurrencyText';
import { BarCode } from './barCode';
import { InvoiceActions } from './InvoiceActions';
import { Recipient } from './recipient';
import { RecipientData } from './recipientData';

/**
 * BillingStandardLayout - Standard template layout (version 1)
 * Displays invoice with the original structure:
 * - Title: FATURA
 * - Section: CLIENTE (two columns: client info + CPF/Address)
 * - Section: FATURA (two columns: creation date + invoice ID)
 * - Section: DETALHES DA FATURA (items with dotted lines)
 */
export const BillingStandardLayout = ({ dataBilling, pdfUrl, pdfLoading, pdfError }) => {
  const totalAmount = dataBilling.invoiceItems?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

  const itemsWithTotal = [
    ...dataBilling.invoiceItems?.map(item => ({
      description: item.description,
      amount: item.amount,
      isTotal: false
    })) || [],
    {
      description: 'TOTAL',
      amount: totalAmount,
      isTotal: true
    }
  ];

  return (
    <>
      <S.Wrapper className='billing'>
        {/* Section: CLIENTE */}
        <div style={{ width: '100%' }}>
          <S.SubTitle style={{ marginBottom: '0', marginTop: '16px', borderBottom: '1px solid #dfe1e6', paddingBottom: '8px', fontSize: '22px' }}>
            Fatura
          </S.SubTitle>
        </div>
      
        <div style={{ display: 'flex', flexDirection: 'row', gap: '32px', width: '100%', marginTop: '16px', marginBottom: '16px' }}>
          {/* Left column: Name and Due Date */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <S.Text>Cliente</S.Text>
              <S.SimpleText>{dataBilling?.payerName}</S.SimpleText>
            </div>
            <div>
              <S.Text>Data de Vencimento</S.Text>
              <S.SimpleText>
                <DateText date={dataBilling.invoiceDueDate} />
              </S.SimpleText>
            </div>
          </div>

          {/* Right column: CPF and Address */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <S.Text>
                <DocumentLabel number={dataBilling.payerDocumentNumber} />
              </S.Text>
              <S.SimpleText>
                <DocumentNumber number={dataBilling.payerDocumentNumber} />
              </S.SimpleText>
            </div>
            <div>
              <S.Text>Endereço</S.Text>
              <S.SimpleText>{dataBilling?.payerAddress}</S.SimpleText>
            </div>
          </div>
        </div>

        {/* Section: FATURA */}
        <div style={{ width: '100%' }}>
          <S.SubTitle style={{ marginBottom: '0', marginTop: '16px', borderBottom: '1px solid #dfe1e6', paddingBottom: '8px' }}>
            Detalhes da Fatura
          </S.SubTitle>
        </div>
        
        <S.InvoiceDetailsRow>
          {/* Left column: Creation Date */}
          <div style={{ flex: 1 }}>
            <S.Text>Data da Emissão</S.Text>
            <S.SimpleText>
              <DateText date={utcToGmt(dataBilling.invoiceCreatedDate)} />
            </S.SimpleText>
          </div>

          {/* Right column: Invoice Identifier */}
          <div style={{ flex: 1 }}>
            <S.Text>Identificador da Fatura</S.Text>
            <S.SimpleText>{dataBilling.invoiceIdentify}</S.SimpleText>
          </div>
        </S.InvoiceDetailsRow>

        {/* Items table */}
        <div style={{ width: '100%', marginBottom: '16px' }}>
          <table style={{
            width: '100%',
            textAlign: 'left',
            border: '1px solid #dfe1e6',
            borderRadius: '8px',
            borderSpacing: 0,
            margin: '0'
          }}>
            <thead style={{ background: '#f3f5f8' }}>
              <tr style={{ height: '40px' }}>
                <th style={{
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1a305a',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  borderRight: '1px solid #dfe1e6'
                }}>
                  Item
                </th>
                <th style={{
                  textAlign: 'right',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1a305a',
                  paddingLeft: '10px',
                  paddingRight: '10px'
                }}>
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              {itemsWithTotal.map((item, index) => (
                <tr key={index} style={{ height: '40px' }}>
                  <td style={{
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    fontSize: '16px',
                    fontWeight: item.isTotal ? '600' : '400',
                    color: item.isTotal ? '#1a305a' : '#4d5e7e',
                    borderBottom: '1px solid #dfe1e6',
                    borderRight: '1px solid #dfe1e6',
                    width: '85%'
                  }}>
                    {item.description}
                  </td>
                  <td style={{
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    textAlign: 'right',
                    fontSize: '16px',
                    fontWeight: item.isTotal ? '600' : '400',
                    color: item.isTotal ? '#1a305a' : '#4d5e7e',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dfe1e6'
                  }}>
                    <CurrencyText value={item.amount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </S.Wrapper>

      {/* Recipient section */}
      <S.Wrapper>
        <S.RecipientWrapper>
          <Recipient dataBilling={dataBilling} />
          <S.Container>
            <RecipientData dataBilling={dataBilling} />
            {dataBilling.invoiceStatus === 'Pending' && (
              <S.Spacer className='spacer' />
            )}
          </S.Container>
          {dataBilling.invoiceStatus === 'Pending' && (
            <S.ContainerBarCode>
              <BarCode billingNumber={dataBilling.invoiceBarCode} />
              <InvoiceActions
                className='copy-and-print'
                dataBilling={dataBilling}
                pdfUrl={pdfUrl}
                pdfLoading={pdfLoading}
                pdfError={pdfError}
              />
            </S.ContainerBarCode>
          )}
        </S.RecipientWrapper>
      </S.Wrapper>
    </>
  );
};
